<template>
  <div class="cat-objeto">
    <!-- Loading state del control de acceso -->
    <div v-if="accessLoading" class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Verificando acceso...</span>
      </div>
    </div>
    
    <!-- Contenido principal cuando se tiene acceso -->
    <div v-else-if="canAccess" class="cat-objeto-content">
      <!-- Toolbar Component -->
      <div class="toolbar-container">
        <ToolbarComponent
          :buttonGroups="[
            {
              id: 'main-actions',
              buttons: [
                {
                  id: 'nuevo',
                  text: 'Nuevo',
                  icon: 'bi bi-plus',
                  variant: 'primary',
                  event: 'new'
                },
                {
                  id: 'actualizar',
                  text: 'Actualizar',
                  icon: 'bi bi-arrow-clockwise',
                  variant: 'secondary',
                  event: 'refresh'
                },
                {
                  id: 'descargar',
                  text: 'Descargar',
                  icon: 'bi bi-download',
                  variant: 'success',
                  event: 'download'
                }
              ]
            }
          ]"
          :rightInfo="`Total: ${objetos.length} registros`"
          @new="addObjeto"
          @refresh="loadObjetos"
          @download="downloadBulk"
        />
      </div>
      
      <!-- Contenido de la página -->
      <div class="page-content">
        <div class="alert alert-info" v-if="loading">Cargando objetos...</div>
        <div class="alert alert-danger" v-if="error">{{ error }}</div>
        
        <div v-if="!loading && !error && (!objetos || objetos.length === 0)">
          <div v-if="!hasEmpresa" class="alert alert-warning">
            Debe elegir una empresa antes de poder continuar.
          </div>
          <div v-else class="alert alert-info">
            No hay objetos. Haga clic en 'Nuevo' para agregar uno.
          </div>
        </div>

        <!-- Tabla con MyDataGrid -->
        <div v-if="!loading && !error && objetos && objetos.length > 0">
          <MyDataGrid
            :columns="dataGridColumns"
            :rows="objetos"
            :enable-pagination="true"
            :per-page="10"
            :page-sizes="[5, 10, 25, 50]"
            :initial-sort="{ key: 'ObjetoId', dir: 'asc' }"
            filter-placeholder="Buscar objetos..."
            :striped="true"
            :compact="true"
            :show-actions="true"
          >
            <!-- Slot de acciones personalizadas -->
            <template #actions="{ row }">
              <button 
                @click="editObjeto(row)" 
                class="btn btn-sm btn-outline-primary me-1"
                title="Editar objeto"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button 
                @click="confirmDelete(row.ObjetoId)" 
                class="btn btn-sm btn-outline-danger"
                title="Eliminar objeto"
              >
                <i class="fas fa-trash"></i>
              </button>
            </template>
          </MyDataGrid>
        </div>

        <div class="mt-4">
          <router-link to="/" class="btn btn-secondary">
            <i class="bi bi-arrow-left me-1"></i> Volver al Menú
          </router-link>
        </div>
      </div>
    </div>
    
    <!-- Mensaje cuando no se tiene acceso -->
    <div v-else class="alert alert-warning text-center">
      <h4>Acceso Restringido</h4>
      <p>No tiene permisos para acceder a esta página.</p>
      <router-link to="/" class="btn btn-primary">Volver al Inicio</router-link>
    </div>

    <!-- Modal para Nuevo/Editar Objeto -->
    <div 
      class="modal fade" 
      :class="{ 'show d-block': showModal }"
      id="objetoModal" 
      tabindex="-1" 
      aria-labelledby="objetoModalLabel" 
      :aria-hidden="!showModal"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="objetoModalLabel">
              <i class="bi bi-plus-circle me-2" v-if="!isEditing"></i>
              <i class="bi bi-pencil me-2" v-else></i>
              {{ isEditing ? 'Editar Objeto' : 'Nuevo Objeto' }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <CatObjetoForm
              :objeto="formData"
              :isEditing="isEditing"
              :loading="modalLoading"
              @submit="handleSubmit"
              @cancel="closeModal"
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { useAccessControl } from '@/composables/useAccessControl'
import CatObjetoService from '../controllers/CatObjetoController.js';
import MyDataGrid from '@/shared/components/MyDataGrid.vue';
import ToolbarComponent from '@/shared/components/ToolbarComponent.vue';
import CatObjetoForm from '../components/CatObjetoForm.vue';

export default {
  name: 'CatObjetoView',
  components: {
    MyDataGrid,
    ToolbarComponent,
    CatObjetoForm
  },
  setup() {
    const { 
      requiresAuth, 
      requiresMenuPermission, 
      canAccess, 
      isLoading: accessLoading 
    } = useAccessControl({
      requiresAuth: true,
      requiresMenuPermission: false
    })

    return {
      requiresAuth,
      requiresMenuPermission,
      canAccess,
      accessLoading
    }
  },
  data() {
    return {
      service: null,
      // Modal state
      showModal: false,
      modalLoading: false,
      isEditing: false,
      formData: {
        ObjetoId: 0,
        Nombre: '',
        TablaEspecifica: ''
      }
    };
  },
  computed: {
    objetos() {
      return this.service ? this.service.objetos : [];
    },
    loading() {
      return this.service ? this.service.loading : false;
    },
    error() {
      return this.service ? this.service.error : null;
    },
    // Configuración de columnas para MyDataGrid
    dataGridColumns() {
      return [
        { key: 'ObjetoId', label: 'ID', sortable: true },
        { key: 'Nombre', label: 'Nombre', sortable: true },
        { key: 'TablaEspecifica', label: 'Tabla Específica', sortable: true }
      ];
    },
    // Verificar si el usuario puede crear objetos
    canCreate() {
      return this.canAccess;
    },
    // Verificar si hay empresa seleccionada
    hasEmpresa() {
      return this.service ? this.service.hasEmpresa : false;
    }
  },
  created() {
    this.service = new CatObjetoService();
    this.service.initialize();
  },
  mounted() {
    // Emitir evento al padre para notificar que esta página tiene toolbar
    this.$emit('toolbar-mounted');
  },
  methods: {
    async loadObjetos() {
      console.log('Cargando objetos...');
      if (this.service) {
        await this.service.listar();
      }
    },
    addObjeto() {
      this.isEditing = false;
      this.openModal();
    },
    async editObjeto(objeto) {
      this.isEditing = true;
      try {
        // Cargar datos completos del objeto
        if (this.service) {
          const response = await this.service.lectura(objeto.ObjetoId);
          if (response && response.data) {
            this.formData = { ...response.data };
          } else {
            this.formData = { ...objeto };
          }
        } else {
          this.formData = { ...objeto };
        }
        this.openModal();
      } catch (error) {
        console.error('Error al cargar objeto:', error);
        this.$alert.error('No se pudo cargar el objeto para edición.');
      }
    },
    confirmDelete(objetoId) {
      console.log('Iniciando confirmación de eliminación para:', objetoId);
      
      // Usar SweetAlert para la confirmación
      this.$alert.confirm(
        `¿Está seguro que desea eliminar el objeto con ID "${objetoId}"?`,
        'Confirmar eliminación',
        { icon: 'warning' }
      ).then((result) => {
        console.log('Resultado de confirmación:', result);
        
        if (result.isConfirmed) {
          this.deleteObjeto(objetoId);
        }
      });
    },
    async deleteObjeto(objetoId) {
      console.log('Ejecutando eliminación para:', objetoId);
      
      try {
        if (this.service) {
          const response = await this.service.borrar(objetoId);
          console.log('Respuesta de eliminación recibida:', response);
          
          // Verificar si la respuesta es exitosa
          if (response && response.success) {
            // Mostrar mensaje de éxito
            this.$alert.success(response.message || 'Objeto eliminado exitosamente');
            // Recargar datos
            this.service.recargar();
          } else {
            // Si no es exitosa, mostrar error
            this.$alert.error(response.message || 'No se pudo eliminar el objeto. Por favor, intente nuevamente.');
          }
        }
      } catch (error) {
        console.error('Error al eliminar objeto:', error);
        this.$alert.error('No se pudo eliminar el objeto. Por favor, intente nuevamente.');
      }
    },
    // Métodos del modal
    openModal() {
      if (!this.isEditing) {
        this.resetForm();
      }
      this.showModal = true;
      document.body.classList.add('modal-open');
    },
    closeModal() {
      this.showModal = false;
      this.modalLoading = false;
      this.isEditing = false;
      this.resetForm();
      document.body.classList.remove('modal-open');
    },
    resetForm() {
      this.formData = {
        ObjetoId: 0,
        Nombre: '',
        TablaEspecifica: ''
      };
    },
    async handleSubmit(formData) {
      this.modalLoading = true;
      try {
        if (this.service) {
          const response = await this.service.guardar(formData);
          
          // Verificar si la respuesta es exitosa
          if (response && response.success) {
            // Mostrar mensaje de éxito
            this.$alert.success(response.message || 'Objeto guardado exitosamente');
            // Cerrar modal y recargar datos
            this.closeModal();
            this.service.recargar();
          } else {
            // Si no es exitosa, mostrar error
            this.$alert.error(response.message || 'No se pudo guardar el objeto. Por favor, intente nuevamente.');
          }
        }
      } catch (error) {
        console.error('Error al guardar objeto:', error);
        this.$alert.error('No se pudo guardar el objeto. Por favor, intente nuevamente.');
      } finally {
        this.modalLoading = false;
      }
    },
    async downloadBulk() {
      try {
        if (this.service) {
          await this.service.downloadBulk();
          this.$alert.success('Descarga iniciada');
        }
      } catch (error) {
        console.error('Error descargando objetos:', error);
        this.$alert.error('No se pudo descargar los objetos. Por favor, intente nuevamente.');
      }
    }
  }
}
</script>

<style scoped>
.cat-objeto {
  min-height: calc(100vh - 200px);
}

.cat-objeto-content {
  padding: 0;
}

.page-content {
  padding: 1rem;
}

/* Modal styles */
.modal.show {
  display: block !important;
}

/* Forzar centrado del modal */
.modal {
  padding: 0 !important;
}

.modal-dialog {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  max-width: 600px;
  width: 90%;
  z-index: 1055;
  max-height: 90vh;
  overflow-y: auto;
}

/* Centrado vertical y horizontal */
.modal.show .modal-dialog {
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
}

/* Responsivo para móviles */
@media (max-width: 768px) {
  .modal-dialog {
    width: 95% !important;
    max-width: none !important;
    max-height: 95vh;
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    width: 98% !important;
    max-height: 98vh;
  }
}
</style>
